import { lazy, StrictMode, Suspense } from "react";
import { createRoot } from "react-dom/client";
import { PublicSite } from "./site/Site";
import "./styles.css";

/**
 * Five surfaces, one entry: the public site at `/`, the golfer scorecard at
 * `/p/{venue}/{course}`, the venue feed at `/tv/{venue}`, the venue admin at
 * `/venue`, and the support desk at `/admin`. Hand-rolled rather than a router
 * library -- the TV must not load a byte it does not need.
 *
 * That last sentence used to be aspirational. Avoiding a router library saved
 * the library, not the code: every surface was in one chunk, so a TV stick
 * downloaded and parsed the whole venue admin -- 162kB of source and 44kB of
 * CSS it can never navigate to -- before it could draw a leaderboard.
 *
 * So the four surfaces nobody reaches by typing the domain are split out.
 * `PublicSite` stays eager on purpose: those pages are prerendered
 * (scripts/prerender.mjs) and `createRoot` replaces that markup on mount, so
 * making it lazy would take a page that already shows its content and blank it
 * for a round trip.
 *
 * The fallback is `null` rather than a spinner. `styles.css` is eager and
 * paints the dark background, so an empty frame reads as "still loading"
 * rather than as a flash of white -- and on a TV that runs unattended, a
 * spinner that gets stuck is worse than a background that does not.
 */
const App = lazy(() => import("./App"));
const TvDisplay = lazy(() =>
  import("./tv/TvDisplay").then((m) => ({ default: m.TvDisplay })),
);
const VenueAdmin = lazy(() =>
  import("./admin/VenueAdmin").then((m) => ({ default: m.VenueAdmin })),
);
const AdminEntry = lazy(() =>
  import("./admin/AdminEntry").then((m) => ({ default: m.AdminEntry })),
);

function route() {
  const parts = window.location.pathname.split("/").filter(Boolean);
  if (parts[0] === "tv" && parts[1]) return <TvDisplay slug={parts[1]} />;
  if (parts[0] === "venue") return <VenueAdmin path={parts[1] ?? ""} />;
  if (parts[0] === "admin") return <AdminEntry />;
  // The golfer scorecard owns /p/{venue}/{course} and nothing else. Everything
  // left over is the public site -- the domain root used to render "Scorecard
  // not found", which is what a prospective customer saw when they typed the
  // address off a business card.
  if (parts[0] === "p") return <App />;
  return <PublicSite />;
}

createRoot(document.getElementById("root")!).render(
  <StrictMode>
    <Suspense fallback={null}>{route()}</Suspense>
  </StrictMode>,
);

// The service worker caches the app shell so a golfer who backgrounds the tab
// in a dead spot can come back to their round. Registered after paint so it
// never delays the first render, and only where it is supported.
if ("serviceWorker" in navigator) {
  window.addEventListener("load", () => {
    void navigator.serviceWorker.register("/sw.js").catch(() => {
      // No service worker means no offline shell. Scoring still works; the
      // round is in IndexedDB either way.
    });
  });
}
